/**
 * useWellnessCheckins — lightweight read of last 7 days of check-ins
 * for the Wellness page. Does NOT duplicate useDailyProgression logic;
 * this is a simple read-only query with no overlay dependency.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMemo } from "react";

export interface WellnessCheckin {
  id: string;
  checkin_date: string;
  training_done: boolean;
  nutrition_done: boolean;
  prevention_done: boolean;
  sleep_hours: number | null;
  pain_flags: { zone: string; intensity: number }[];
}

function getLast7Days(): string[] {
  const days: string[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split("T")[0]);
  }
  return days;
}

export function useWellnessCheckins() {
  const { user } = useAuth();

  const last7Days = useMemo(() => getLast7Days(), []);
  const startDate = last7Days[0];
  const todayStr = last7Days[last7Days.length - 1];

  const { data: checkins, isLoading } = useQuery({
    queryKey: ["wellness-checkins", user?.id, startDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("program_checkins")
        .select("id, checkin_date, training_done, nutrition_done, prevention_done, sleep_hours, pain_flags")
        .eq("user_id", user!.id)
        .gte("checkin_date", startDate)
        .lte("checkin_date", todayStr)
        .order("checkin_date", { ascending: true });

      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        checkin_date: r.checkin_date,
        training_done: r.training_done,
        nutrition_done: r.nutrition_done,
        prevention_done: r.prevention_done,
        sleep_hours: r.sleep_hours,
        pain_flags: (r.pain_flags as any[]) || [],
      })) as WellnessCheckin[];
    },
    enabled: !!user,
  });

  const todayCheckin = useMemo(
    () => (checkins ?? []).find((c) => c.checkin_date === todayStr) ?? null,
    [checkins, todayStr]
  );

  // Nutrition dots for last 7 days
  const nutritionDots = useMemo(() => {
    return last7Days.map((date) => {
      const c = (checkins ?? []).find((ch) => ch.checkin_date === date);
      return { date, done: c?.nutrition_done ?? false };
    });
  }, [checkins, last7Days]);

  // Pain zones aggregated this week
  const painZones = useMemo(() => {
    const zoneMap = new Map<string, number>();
    for (const c of checkins ?? []) {
      for (const pf of c.pain_flags) {
        zoneMap.set(pf.zone, (zoneMap.get(pf.zone) ?? 0) + 1);
      }
    }
    return Array.from(zoneMap.entries())
      .map(([zone, count]) => ({ zone, count }))
      .sort((a, b) => b.count - a.count);
  }, [checkins]);

  // Recovery score
  const recoveryScore = useMemo(() => {
    const sleepEntries = (checkins ?? []).filter((c) => c.sleep_hours != null);
    const avgSleep =
      sleepEntries.length > 0
        ? sleepEntries.reduce((s, c) => s + (c.sleep_hours ?? 0), 0) / sleepEntries.length
        : null;
    const totalPainCount = (checkins ?? []).reduce(
      (s, c) => s + c.pain_flags.length,
      0
    );

    if (avgSleep === null) return { level: "unknown" as const, avgSleep: null, painCount: totalPainCount };

    if (avgSleep >= 7 && totalPainCount <= 1)
      return { level: "good" as const, avgSleep, painCount: totalPainCount };
    if ((avgSleep >= 6 && avgSleep < 7) || (totalPainCount >= 2 && totalPainCount <= 3))
      return { level: "medium" as const, avgSleep, painCount: totalPainCount };
    return { level: "attention" as const, avgSleep, painCount: totalPainCount };
  }, [checkins]);

  return {
    checkins: checkins ?? [],
    isLoading,
    todayCheckin,
    nutritionDots,
    painZones,
    recoveryScore,
    last7Days,
  };
}
